/* eslint-disable jsx-a11y/anchor-is-valid */
import "./StripePlanSelectionPage.scss";

import { Button, Flex, InfoIcon, Text, Tooltip } from "@fluentui/react-northstar";
import { StandardPlanLevels, StripeSubType } from "@teams/types";
import { UserDTO, UserSubscriptionDTO } from "@teams/types/api";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { PollyIcon } from "../../_shared/components/PollyIcon";
import { clientEventTracker } from "../../_shared/utils/clientTelemetry";
import { getPlanType } from "../../_shared/utils/paymentHelpers";

type PlanExtendedProps = {
  planFeatures?: string[];
};

type props = {
  pricingPlans: StandardPlanLevels[];
  isTrial: boolean;
  currentSubscription: UserSubscriptionDTO;
  currentSubType: StripeSubType;
  user: UserDTO;
  isDarkTheme: boolean;
  classForDarkTheme: string;
  onPlanSelected: (planId: string) => void;
  setTrial: (isTrial: boolean) => void;
  trialExpiresAt?: Date;
};

export const StripePlanSelectionPage: React.FC<props> = ({
  pricingPlans,
  isTrial,
  currentSubscription,
  currentSubType,
  user,
  isDarkTheme,
  classForDarkTheme,
  setTrial,
  onPlanSelected,
  trialExpiresAt,
}) => {
  const [trialAvailable, setTrialAvailable] = useState<boolean>(false);

  const plansWithExtendedProps = useMemo(() => {
    return getPlansForPricingPage(pricingPlans);
  }, [pricingPlans]);

  const currentPlanText = useMemo(() => {
    switch (currentSubType) {
      case "free":
        return (
          <>
            You are currently on a <b>Free Plan</b>
          </>
        );
      case "trial":
        return (
          <>
            You are currently on a <b>Trial</b>
          </>
        );
      case "trial-expired":
        return (
          <>
            You are currently on a <b>Free Plan</b>.{" "}
            <p style={{ color: "#f59e0b", display: "inline" }}>Your trial has expired</p>
          </>
        );
      case "pro":
        return (
          <>
            You are currently on <b>Pro Plan</b>
          </>
        );
      case "standard":
        return (
          <>
            You are currently on <b>Standard Plan</b>
          </>
        );
      case "legacy":
        return (
          <>
            You are currently on a <b>{currentSubscription.planLevel} Responses per month Plan</b>
          </>
        );
      case "expired":
        let previousPlan = null;
        if (currentSubscription.scope === "tenant") {
          previousPlan = "Enterprise";
        } else if (currentSubscription.planLevel === 500) {
          previousPlan = "Standard";
        } else if (currentSubscription.planLevel && currentSubscription.planLevel >= 2500) {
          previousPlan = "Pro";
        } else if (currentSubscription.planLevel === Number.MAX_SAFE_INTEGER) {
          previousPlan = "Unlimited";
        } else {
          previousPlan = `${currentSubscription.planLevel} Responses per month`;
        }

        return (
          <>
            You were previously on a <b>{previousPlan} Plan</b>.{" "}
            <p style={{ color: "#f59e0b", display: "inline" }}>Your subscription has expired</p>
          </>
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSubType]);

  useEffect(() => {
    if (currentSubType === "free") {
      setTrialAvailable(true);
    } else {
      setTrial(false);
    }
  }, [currentSubType, setTrial]);

  return (
    <Flex column hAlign="center" className="plans-container">
      <Flex
        hAlign="start"
        vAlign="stretch"
        className={`plans-container__info-container${classForDarkTheme}`}
      >
        <Flex gap="gap.small">
          <InfoIcon size="medium" outline />

          {currentSubType === "trial" ? (
            <Tooltip
              align="start"
              position="above"
              content={`You’ll be switched to Free Plan after ${moment(trialExpiresAt).fromNow(
                true
              )}.`}
            >
              <Text content={currentPlanText} />
            </Tooltip>
          ) : (
            <Text content={currentPlanText} />
          )}
        </Flex>
        {trialAvailable && (
          <Flex.Item push>
            <Text
              className="plans-container__info-container__link-text"
              content={
                isTrial ? (
                  <>
                    Ready to buy?{" "}
                    <Button
                      text
                      primary
                      content={"Buy Now"}
                      className="plans-container__info-container__link-button"
                      onClick={() => {
                        setTrial(false);
                      }}
                    />
                  </>
                ) : (
                  <>
                    Not ready to buy?{" "}
                    <Button
                      text
                      primary
                      content={"Start a Free Trial"}
                      className="plans-container__info-container__link-button"
                      onClick={() => {
                        setTrial(true);
                      }}
                    />
                  </>
                )
              }
            />
          </Flex.Item>
        )}
      </Flex>
      <Flex fill space="around" hAlign="center" className="pricing-plans-container">
        {plansWithExtendedProps.map(plan => {
          return (
            <PricingPlanCard
              id={plan.id}
              name={plan.name}
              monthlyPrice={plan.monthlyPrice}
              yearlyPrice={plan.yearlyPrice}
              responseQuantity={plan.responseQuantity}
              planFeatures={plan.planFeatures}
              currentSubType={currentSubType}
              trialMode={isTrial}
              user={user}
              currentPlanId={currentSubscription?.providerSubscriptionMetadata?.planId}
              key={plan.id}
              isDarkTheme={isDarkTheme}
              classForDarkTheme={classForDarkTheme}
              onPlanSelected={onPlanSelected}
            />
          );
        })}
      </Flex>
      <Flex fill className="enterprise-container">
        <Flex gap="gap.small" vAlign="center">
          <PollyIcon
            icon="message-icon"
            className="enterprise-container__pro-message-icon"
            inheritColor
          />
          <Text className={`enterprise-container__enterprise-text${classForDarkTheme}`}>
            Need to buy a large number of licenses for your team?
          </Text>
        </Flex>
        <Button
          content={"Talk to Sales"}
          tinted
          className={`enterprise-container____enterprise-button${classForDarkTheme}`}
          onClick={() => {
            clientEventTracker.track("Plan Selected", user, {
              plan: "Enterprise",
              type: "Talk to Sales",
            });
            window.open("https://polly.ai/talk-to-sales", "_blank");
            return;
          }}
        />
      </Flex>
      <Flex className="enterprise-container__faq-text-container">
        <Text className={`enterprise-container__faq-text${classForDarkTheme}`}>
          See a full feature comparison and FAQs{" "}
          <a
            href="#"
            className={"faqs-link"}
            onClick={e => {
              e.preventDefault();
              window.open("https://www.polly.ai/microsoft-teams-pricing#faqs", "_blank");
            }}
          >
            here
          </a>
        </Text>
      </Flex>
    </Flex>
  );
};

type pricingPlanCardProps = Partial<StandardPlanLevels> &
  PlanExtendedProps & {
    trialMode: boolean;
    currentSubType: StripeSubType;
    currentPlanId?: string;
    user: UserDTO;
    isDarkTheme: boolean;
    classForDarkTheme: string;
    onPlanSelected: (planId: string) => void;
  };
export const PricingPlanCard: React.FC<pricingPlanCardProps> = ({
  id,
  name,
  monthlyPrice,
  yearlyPrice,
  planFeatures,
  trialMode,
  currentSubType,
  currentPlanId,
  user,
  classForDarkTheme,
  onPlanSelected,
}) => {
  const billingAmountText = useMemo(() => {
    return `Billed $${yearlyPrice} annually`;
  }, [yearlyPrice]);

  const buttonText = useMemo(() => {
    if (id === "enterprise") {
      return "Talk to Sales";
    }
    if (trialMode) {
      return "Start Trial";
    }
    if (currentSubType === "pro") {
      if (id === currentPlanId) {
        return "Current Plan";
      } else {
        return "Downgrade";
      }
    }
    if (currentSubType === "standard") {
      if (id === currentPlanId) {
        return "Current Plan";
      } else {
        return "Upgrade";
      }
    }

    if (id === "teams-standard-500") {
      return "Choose Standard";
    } else if (id === "teams-pro-unlimited") {
      return "Choose Pro";
    }
    return "Choose Standard";
  }, [currentPlanId, currentSubType, id, trialMode]);

  const planType = useMemo(() => {
    return getPlanType(id);
  }, [id]);

  const logTelemetry = useCallback(() => {
    if (trialMode) {
      clientEventTracker.track("Start Trial Clicked", user, {
        plan: name,
      });
      return;
    }

    let checkoutType = null;
    if (id === "enterprise") {
      checkoutType = "Talk to Sales";
    } else {
      switch (currentSubType) {
        case "free":
          checkoutType = "Hard Buy";
          break;
        case "standard":
          checkoutType = "Upgrade";
          break;
        case "pro":
          checkoutType = "Downgrade";
          break;
        case "trial-expired":
          checkoutType = "Lapsed Trial";
          break;
        case "expired":
          checkoutType = "Expired Subscription";
          break;
        case "legacy":
          checkoutType = "Upgrade from legacy";
          break;
        case "trial":
          checkoutType = "On Trial";
          break;
      }
    }

    clientEventTracker.track("Plan Selected", user, {
      plan: name,
      type: checkoutType,
    });
  }, [currentSubType, id, name, trialMode, user]);

  return (
    <Flex
      column
      className={`plan-card${planType === "Pro" ? " plan-card__pro" : ""}${classForDarkTheme}`}
    >
      <Flex column hAlign="center" gap="gap.medium">
        <Text content={name} align="center" className={`plan-card__title${classForDarkTheme}`} />
        <Flex vAlign="start" className="plan-card__price-container">
          <Text content="$" className={`plan-card__dollar-symbol${classForDarkTheme}`} />
          <Text
            content={monthlyPrice}
            align="center"
            className={`plan-card__price${classForDarkTheme}`}
          />
          <Text
            content="/month"
            align="center"
            className={`plan-card__price-subtitle${classForDarkTheme}`}
          />
        </Flex>
        <Text
          content={billingAmountText}
          align="center"
          className={`plan-card__sub-title${classForDarkTheme}`}
        />
      </Flex>
      <Button
        content={buttonText}
        disabled={
          id === currentPlanId && !["free", "trial", "trial-expired"].includes(currentSubType)
        }
        primary={planType === "Pro"}
        tinted={planType !== "Pro"}
        fluid
        className={`plan-card__cta-button ${planType === "Pro" ? "pro" : ""} ${
          planType === "Pro" && trialMode ? "pro-trial" : ""
        }${planType !== "Pro" ? classForDarkTheme : ""}`}
        onClick={() => {
          logTelemetry();
          onPlanSelected(id!);
        }}
      />
      <Flex column className="plan-card__features">
        {planFeatures?.map((feature, index) => {
          return (
            <Flex vAlign="center" gap="gap.small" key={index}>
              <PollyIcon
                icon="blue-check-icon"
                size="small"
                inheritColor={true}
                style={{ color: "#008FF5" }}
              />
              <Text content={feature} className={`plan-card__plan-features${classForDarkTheme}`} />
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};

function getPlansForPricingPage(
  planLevels: StandardPlanLevels[]
): (Partial<StandardPlanLevels> & PlanExtendedProps)[] {
  const plansWithExtendedProps = planLevels.map(plan => {
    const updatedPlan: StandardPlanLevels & PlanExtendedProps = { ...plan };
    if (getPlanType(plan.id) === "Standard") {
      updatedPlan.planFeatures = [
        "500 monthly responses",
        "Unlimited results history",
        "Q&A in Meetings",
        "Icebreakers in Meetings",
        "Quizzes & Trivia"
      ];
    }

    if (getPlanType(plan.id) === "Pro") {
      updatedPlan.planFeatures = [
        "Everything in Standard, plus:",
        "Unlimited responses",
        "Send pollys to chat messages",
        "Buy licenses for your team",
        "Priority support",
      ];
    }

    return updatedPlan as Partial<StandardPlanLevels> & PlanExtendedProps;
  });

  return plansWithExtendedProps;
}
